import React, { useEffect, useState } from 'react';
import { getProjectOverview } from '../../projectService';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/Popover';
import {
  FolderKanban,
  CheckCircle,
  AlertCircle,
  Clock,
  Database,
  Info,
  FileText,
  History,
  Printer,
  PlusCircle,
  RefreshCw,
  ShieldCheck,
  ArrowRight,
  PauseCircle,
  FileType,
} from 'lucide-react';
import ProjectDataFlow from './ProjectDataFlow';

const ProjectOverview = ({
  projectId,
  data: initialData = null,
}: {
  projectId?: any;
  data?: any;
}) => {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      setError(null);
      getProjectOverview(projectId)
        .then((res) => {
          setOverview(res.data);
        })
        .catch((err) => {
          console.error('Failed to fetch project overview:', err);
          setError('Nepodařilo se načíst přehled akce.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [projectId]);

  if (loading && !overview) {
    return (
      <div className="flex justify-center items-center h-64">
        <SpinnerOverlay size="lg" tip="Načítám přehled akce..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="m-4 border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <EmptyState title={error as string} />
        </CardContent>
      </Card>
    );
  }

  if (!overview && !initialData) return null;

  const { stats, documentations, classifications, appearances, metadata } = overview || {
    metadata: initialData,
  };
  const d = initialData || metadata;

  return (
    <div className="p-0 space-y-6 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex justify-between items-start bg-white p-6 rounded-xl shadow-xs border border-gray-100">
        <div>
          <div className="flex items-center gap-3">
            <h2
              className="text-xl font-semibold tracking-tight text-neutral-800"
              style={{ margin: 0 }}
            >
              {d?.identifier} - {d?.title}
            </h2>
            <div className="flex items-center gap-2">
              {d?.is_active ? (
                <Badge variant="success">
                  <CheckCircle className="h-3 w-3" /> Aktivní
                </Badge>
              ) : (
                <Badge variant="default">Neaktivní</Badge>
              )}
              {d?.is_archived && <Badge variant="warning">Archivováno</Badge>}
            </div>
          </div>
          <p className="mt-2 text-lg text-neutral-500">{d?.name_official}</p>
          <div className="flex items-center gap-2">
            <span className="text-neutral-700">
              <Info className="h-4 w-4 inline" /> {d?.etape?.title || metadata?.building_title}
            </span>
            <Separator orientation="vertical" className="h-4" />
            <span className="text-neutral-700">
              <Database className="h-4 w-4 inline" /> {d?.culture?.title || 'Výchozí kultura'}
            </span>
          </div>
        </div>
      </div>

      {overview && (
        <>
          {/* TypeMark Workflow Pipeline */}
          <Card className="rounded-xl shadow-xs bg-white overflow-hidden">
            <CardHeader>
              <CardTitle>Průběh zpracování TypeMarků (Procesní linka)</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Top Ratio Bar */}
              <div className="flex h-3 w-full rounded-full overflow-hidden bg-gray-100 mb-8 shadow-inner">
                <div
                  style={{ width: `${(stats.status_new / stats.total_types) * 100}%` }}
                  className="bg-blue-300 transition-all"
                />
                <div
                  style={{ width: `${(stats.status_in_progress / stats.total_types) * 100}%` }}
                  className="bg-orange-400 transition-all"
                />
                <div
                  style={{ width: `${(stats.status_done / stats.total_types) * 100}%` }}
                  className="bg-green-500 transition-all"
                />
                <div
                  style={{ width: `${(stats.status_checked / stats.total_types) * 100}%` }}
                  className="bg-teal-500 transition-all"
                />
                <div
                  style={{ width: `${(stats.status_postponed / stats.total_types) * 100}%` }}
                  className="bg-gray-400 transition-all"
                />
                <div
                  style={{ width: `${(stats.status_needs_edit / stats.total_types) * 100}%` }}
                  className="bg-red-500 transition-all"
                />
              </div>

              {/* Main Pipeline Flow */}
              <div className="flex flex-wrap items-center justify-center gap-4 relative">
                {/* 1. NEW */}
                <div className="w-full md:w-auto md:flex-1">
                  <div className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-blue-50 border-2 border-blue-200 flex items-center justify-center mb-2 group-hover:bg-blue-500 group-hover:text-white transition-all">
                      <PlusCircle className="h-5 w-5" />
                    </div>
                    <span className="font-semibold text-[11px] uppercase text-neutral-800">
                      Nové
                    </span>
                    <h4 className="text-base font-semibold text-neutral-800 m-0">
                      {stats.status_new}
                    </h4>
                  </div>
                </div>

                <div className="hidden md:block text-center text-gray-200">
                  <ArrowRight className="h-4 w-4" />
                </div>

                {/* 2. IN PROGRESS */}
                <div className="w-full md:w-auto md:flex-1">
                  <div className="flex flex-col items-center group">
                    <div
                      className={`w-14 h-14 rounded-full bg-orange-50 border-2 border-orange-200 flex items-center justify-center mb-2 group-hover:bg-orange-400 group-hover:text-white transition-all ${
                        stats.status_in_progress > 0 ? 'shadow-md shadow-orange-100' : ''
                      }`}
                    >
                      <RefreshCw className="h-6 w-6" />
                    </div>
                    <span className="font-semibold text-[11px] uppercase text-neutral-800">
                      Rozpracované
                    </span>
                    <h4 className="text-base font-semibold text-neutral-800 m-0">
                      {stats.status_in_progress}
                    </h4>
                  </div>
                </div>

                <div className="hidden md:block text-center text-gray-200">
                  <ArrowRight className="h-4 w-4" />
                </div>

                {/* 3. DONE */}
                <div className="w-full md:w-auto md:flex-1">
                  <div className="flex flex-col items-center group">
                    <div className="w-12 h-12 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mb-2 group-hover:bg-green-500 group-hover:text-white transition-all">
                      <CheckCircle className="h-5 w-5" />
                    </div>
                    <span className="font-semibold text-[11px] uppercase text-neutral-800">
                      Hotové
                    </span>
                    <h4 className="text-base font-semibold text-neutral-800 m-0">
                      {stats.status_done}
                    </h4>
                  </div>
                </div>

                <div className="hidden md:block text-center text-gray-200">
                  <ArrowRight className="h-4 w-4" />
                </div>

                {/* 4. CHECKED */}
                <div className="w-full md:w-auto md:flex-1">
                  <div className="flex flex-col items-center group">
                    <div className="w-14 h-14 rounded-full bg-teal-50 border-2 border-teal-200 flex items-center justify-center mb-2 group-hover:bg-teal-500 group-hover:text-white transition-all shadow-lg shadow-teal-50">
                      <ShieldCheck className="h-6 w-6" />
                    </div>
                    <span className="font-semibold text-[11px] uppercase text-teal-600">
                      Zkontrolované
                    </span>
                    <h3 className="text-lg font-semibold text-teal-700 m-0">
                      {stats.status_checked}
                    </h3>
                  </div>
                </div>

                {/* EXCEPTIONS */}
                <div className="w-full md:w-auto mt-8 md:mt-0 md:border-l md:pl-6 border-gray-100">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-50">
                      <div className="flex items-center gap-2">
                        <PauseCircle className="h-4 w-4 text-gray-400" />
                        <span className="text-[11px] text-neutral-700">Odložené</span>
                      </div>
                      <span className="font-semibold text-neutral-800">
                        {stats.status_postponed}
                      </span>
                    </div>
                    <div
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        stats.status_needs_edit > 0
                          ? 'bg-red-50 border border-red-100 animate-pulse'
                          : 'bg-white border border-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <AlertCircle
                          className={`h-4 w-4 ${
                            stats.status_needs_edit > 0 ? 'text-red-500' : 'text-gray-400'
                          }`}
                        />
                        <span className="text-[11px] text-neutral-700">K úpravě</span>
                      </div>
                      <span
                        className={`font-semibold ${
                          stats.status_needs_edit > 0 ? 'text-red-600' : 'text-neutral-800'
                        }`}
                      >
                        {stats.status_needs_edit}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documentation Row */}
          <Card className="rounded-xl shadow-xs">
            <CardHeader>
              <CardTitle>Projektová dokumentace</CardTitle>
            </CardHeader>
            <CardContent>
              {documentations && documentations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {documentations.map((doc) => (
                    <div
                      key={doc.id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-blue-200 transition-all h-full"
                    >
                      <div className="flex flex-col gap-2 w-full">
                        <div className="flex justify-between items-start">
                          <span className="font-semibold text-lg text-neutral-800">
                            {doc.title}
                          </span>
                          <Badge variant="brand">{doc.phase_shortcut}</Badge>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-neutral-500">
                            <Info className="h-4 w-4 inline" /> HIP:{' '}
                            <span className="font-semibold text-neutral-800">
                              {doc.chief_engineer_name || '-'}
                            </span>
                          </span>
                          <span className="text-xs text-neutral-500">
                            <FolderKanban className="h-4 w-4 inline" /> Stavebník:{' '}
                            <span className="font-semibold text-neutral-800">
                              {doc.builder_name || '-'}
                            </span>
                          </span>
                          {doc.publication_data && (
                            <span className="text-xs text-neutral-500">
                              <Clock className="h-4 w-4 inline" /> Datum vydání:{' '}
                              <span className="font-semibold text-neutral-800">
                                {new Date(doc.publication_data).toLocaleDateString('cs-CZ')}
                              </span>
                            </span>
                          )}

                          <div className="mt-3">
                            <span className="block mb-1 text-xs text-neutral-500">
                              <FileText className="h-4 w-4 inline" /> Seznam listů (
                              {doc.sheets?.length || 0}):
                            </span>
                            <div className="space-y-2">
                              {doc.sheets && doc.sheets.length > 0 ? (
                                doc.sheets.map((sheet) => (
                                  <div
                                    key={sheet.id}
                                    className="flex items-center justify-between bg-white p-2 rounded border border-gray-100 hover:border-blue-200 transition-all"
                                  >
                                    <div className="flex flex-col gap-0">
                                      <span className="text-[11px] font-medium leading-tight text-neutral-800">
                                        {sheet.reference_number
                                          ? `${sheet.reference_number}: `
                                          : ''}
                                        {sheet.title}
                                      </span>
                                      <div className="flex flex-col space-y-0.5">
                                        <span
                                          className="text-neutral-500"
                                          style={{ fontSize: '9px' }}
                                        >
                                          Poslední verze: {sheet.versions?.[0]?.version_number}.
                                          {sheet.versions?.[0]?.revision_number || 0}
                                        </span>
                                        {sheet.last_printed_on && (
                                          <span
                                            style={{
                                              fontSize: '10px',
                                              color: '#1890ff',
                                              fontWeight: '500',
                                            }}
                                            className="flex items-center mt-1"
                                          >
                                            <Printer className="h-4 w-4 inline mr-1" /> Tisk:{' '}
                                            {new Date(sheet.last_printed_on).toLocaleString(
                                              'cs-CZ'
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      {sheet.versions && sheet.versions.length > 1 && (
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="p-0 h-auto text-[9px] text-blue-500 hover:underline"
                                              data-testid={`project-overview-sheet-history-btn-${sheet.id}`}
                                            >
                                              (zobrazit historii {sheet.versions.length})
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-80" align="start">
                                            <div className="text-sm font-semibold text-neutral-800 mb-2 pb-2 border-b border-neutral-150">
                                              Historie verzí
                                            </div>
                                            <div className="max-h-48 overflow-y-auto space-y-1">
                                              {sheet.versions.map((v) => (
                                                <div
                                                  key={v.id}
                                                  className="flex justify-between items-center p-2 hover:bg-gray-50 rounded text-[10px]"
                                                >
                                                  <div className="flex flex-col gap-0">
                                                    <div className="flex items-center gap-2">
                                                      <History className="h-4 w-4 text-blue-400" />
                                                      <span className="text-neutral-700">
                                                        v{v.version_number}.{v.revision_number}
                                                      </span>
                                                      <span className="text-xs text-neutral-500">
                                                        (
                                                        {new Date(v.created_on).toLocaleDateString(
                                                          'cs-CZ'
                                                        )}
                                                        )
                                                      </span>
                                                    </div>
                                                    {v.printed_on && (
                                                      <span
                                                        style={{
                                                          fontSize: '9px',
                                                          color: '#1890ff',
                                                        }}
                                                      >
                                                        <Printer className="h-4 w-4 inline mr-1" />{' '}
                                                        Tisk:{' '}
                                                        {new Date(v.printed_on).toLocaleString(
                                                          'cs-CZ'
                                                        )}
                                                      </span>
                                                    )}
                                                  </div>
                                                  <Button
                                                    variant="link"
                                                    size="sm"
                                                    asChild
                                                    data-testid={`project-overview-version-file-btn-${v.id}`}
                                                  >
                                                    <a
                                                      href={v.file_url}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                    >
                                                      <FileText className="h-4 w-4" />
                                                    </a>
                                                  </Button>
                                                </div>
                                              ))}
                                            </div>
                                          </PopoverContent>
                                        </Popover>
                                      )}
                                    </div>
                                    {sheet.latest_file_url && (
                                      <Button
                                        size="sm"
                                        className="rounded-md"
                                        asChild
                                        data-testid={`project-overview-sheet-open-btn-${sheet.id}`}
                                      >
                                        <a
                                          href={sheet.latest_file_url}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          <FileType className="h-4 w-4" />
                                          Otevřít
                                        </a>
                                      </Button>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <span className="text-xs italic text-neutral-500">Žádné listy</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="K této akci zatím není přiřazena žádná dokumentace" />
              )}
            </CardContent>
          </Card>

          {/* Mapa datových vazeb */}
          {overview && <ProjectDataFlow data={overview} />}

          {/* Classification and Appearances Rows */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="rounded-xl shadow-xs h-full">
              <CardHeader>
                <CardTitle>TypeMarky dle zatřídění</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto pr-2">
                  {classifications && classifications.length > 0 ? (
                    <div className="space-y-2">
                      {classifications.map((c) => (
                        <div
                          key={c.classification__id}
                          className="p-3 bg-white border border-gray-100 rounded-lg flex justify-between items-center hover:shadow-xs transition-all"
                        >
                          <div className="flex flex-col gap-0">
                            <Badge variant="brand" size="sm" className="m-0 font-mono text-[10px]">
                              {c.classification__code || 'bez kódu'}
                            </Badge>
                            <span className="font-semibold text-xs text-neutral-800">
                              {c.classification__title}
                            </span>
                          </div>
                          <Badge variant="solidBrand" size="sm">
                            {c.count}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState title="Žádné zatříděné TypeMarky" compact />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl shadow-xs h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Seznam vzhledů a barev
                  <Badge variant="solidBrand" size="sm">
                    {stats.total_appearances}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto pr-2">
                  {appearances && appearances.length > 0 ? (
                    <div className="space-y-2">
                      {appearances.map((app) => (
                        <div
                          key={`${app.sys_title}-${app.title}`}
                          className="p-3 bg-white border border-gray-100 rounded-lg hover:shadow-xs transition-all"
                        >
                          <div className="flex flex-col gap-0 w-full">
                            <div className="flex justify-between items-center w-full">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-6 h-6 rounded-md border border-gray-100 shadow-inner"
                                  style={{ backgroundColor: app.hex || '#ffffff' }}
                                />
                                <div className="flex flex-col">
                                  <span className="font-semibold text-[11px] leading-tight text-neutral-800">
                                    {app.title || app.sys_title}
                                  </span>
                                  {app.title && app.title !== app.sys_title && (
                                    <span className="text-[9px] uppercase tracking-tighter text-neutral-500">
                                      {app.sys_title}
                                    </span>
                                  )}
                                  {(app.pattern_title || app.finish_title) && (
                                    <span className="text-[10px] text-neutral-500">
                                      {[app.pattern_title, app.finish_title]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {app.hex && (
                                <span className="text-[9px] font-mono text-neutral-500">
                                  {app.hex}
                                </span>
                              )}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {app.models?.map((m) => (
                                <Badge
                                  key={m}
                                  variant="brand"
                                  size="sm"
                                  className="text-[8px] m-0 bg-white border-cyan-100 text-cyan-600"
                                >
                                  <Database className="h-2 w-2 mr-1 inline" /> {m}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState title="Žádné definované vzhledy" compact />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectOverview;
